import { useEffect, useRef } from 'react';
import './TurnstileWidget.css';

// Cloudflare Turnstile widget (managed mode, explicit rendering). Calls onToken with a fresh
// token when the challenge is passed, and with undefined when the token expires or errors, so
// the parent can disable its submit button until a new token arrives.
//
// Dev fallback: without REACT_APP_TURNSTILE_SITE_KEY (or with a '<placeholder>' value) the widget
// renders nothing and immediately reports a 'dev-bypass' token — servers without a configured
// turnstileSecret skip verification, so this passes in dev/CI. A sitekey-less frontend against a
// secret-configured server is a configuration error (the placeholder token would be rejected).

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileWidgetProps {
  onToken: (token: string | undefined) => void;
  /** Analytics action name shown in the Cloudflare dashboard (e.g. 'signup-tos', 'report'). */
  action?: string;
  /** Change this value to force a reset of the widget (e.g. after a server-side verify failure). */
  resetSignal?: number;
  className?: string;
}

function getSiteKey(): string | undefined {
  const key = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  return key && !key.startsWith('<') ? key : undefined;
}

/** Whether this build has a Turnstile sitekey. False means the widget auto-passes a 'dev-bypass'
 * token, which only works against a server WITHOUT a turnstileSecret — callers can use this to
 * show a configuration error instead of a pointless "try again" when such a server rejects. */
export function isTurnstileConfigured(): boolean {
  return !!getSiteKey();
}

function loadTurnstileScript(onReady: () => void): void {
  if ((window as any).turnstile) {
    onReady();
    return;
  }
  const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);
  if (existing) {
    existing.addEventListener('load', onReady);
    return;
  }
  const s = document.createElement('script');
  s.src = TURNSTILE_SCRIPT_URL;
  s.async = true;
  s.onload = onReady;
  document.head.appendChild(s);
}

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ onToken, action, resetSignal, className }) => {
  const siteKey = getSiteKey();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (siteKey) return;
    console.warn('TurnstileWidget: REACT_APP_TURNSTILE_SITE_KEY is not set in this build - '
      + 'sending a dev-bypass token. This fails against a server with a configured turnstileSecret. '
      + '(After editing .env, the dev server must be restarted.)');
    onTokenRef.current('dev-bypass');
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey) return;
    let unmounted = false;
    loadTurnstileScript(() => {
      const turnstile = (window as any).turnstile;
      if (unmounted || !turnstile || !containerRef.current || widgetIdRef.current != null) return;
      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme: 'auto',
        callback: (token: string) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(undefined),
        'error-callback': () => onTokenRef.current(undefined),
      });
    });
    return () => {
      unmounted = true;
      if (widgetIdRef.current != null) {
        (window as any).turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = undefined;
      }
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (!resetSignal || widgetIdRef.current == null) return;
    onTokenRef.current(undefined);
    (window as any).turnstile?.reset(widgetIdRef.current);
  }, [resetSignal]);

  if (!siteKey) return null;
  return <div ref={containerRef} className={`turnstileWidget${className ? ' ' + className : ''}`} />;
};

export default TurnstileWidget;
